import { Location } from '@angular/common';
import { isNil, omitBy } from 'lodash';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  type OnDestroy,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  getSchemaSize,
  initVersionedSchema,
  type MixedSchema,
} from '@principle-theorem/editor';
import { EditorPresetsService } from '@principle-theorem/ng-interactions';
import {
  CurrentBrandScope,
  CurrentPatientScope,
  OrganisationService,
  PdfService,
} from '@principle-theorem/ng-principle-shared';
import {
  confirmationDialogData,
  ConfirmDialogComponent,
  DialogPresets,
  type IConfirmationDialogData,
  type IConfirmationDialogInput,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { Practice, SystemTemplates } from '@principle-theorem/principle-core';
import {
  type IBrand,
  type IDocumentSettings,
  type IPatientDocument,
  type IPDFContent,
  type IResolvedTemplateWithName,
  type ITemplateDefinitionWithName,
  SystemTemplate,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  snapshot,
  wakeUpFunction,
  type WithRef,
} from '@principle-theorem/shared';
import { AnyExtension } from '@tiptap/core';
import { type Observable, Subject } from 'rxjs';
import {
  debounceTime,
  map,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { CurrentPatientDocumentScope } from './current-patient-document-scope';
import {
  DocumentPdfDialogComponent,
  DocumentPdfAction,
  DocumentPdfDialogMode,
  type IDocumentPdfDialogData,
  type IDocumentPdfDialogResult,
} from './document-pdf-dialog/document-pdf-dialog.component';

@Component({
  selector: 'pr-patient-document',
  templateUrl: './patient-document.component.html',
  styleUrls: ['./patient-document.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PatientDocumentComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _brandScope = inject(CurrentBrandScope);
  private _patientScope = inject(CurrentPatientScope);
  private _organisation = inject(OrganisationService);
  private _snack = inject(MatSnackBar);
  private _editorExtensions = inject(EditorPresetsService);
  public pdfService = inject(PdfService);

  patientDocument$: Observable<WithRef<IPatientDocument>>;
  documentSettings$: Observable<IDocumentSettings>;
  templateForm = new TypedFormGroup<Pick<IPatientDocument, 'content' | 'name'>>(
    {
      name: new TypedFormControl<string>('', Validators.required),
      content: new TypedFormControl<MixedSchema>(
        initVersionedSchema(),
        Validators.required
      ),
    }
  );

  constructor(
    patientDocumentScope: CurrentPatientDocumentScope,
    private _dialog: MatDialog,
    private _location: Location
  ) {
    this.patientDocument$ = patientDocumentScope.doc$.pipe(filterUndefined());

    this.documentSettings$ = this._organisation.practice$.pipe(
      map((practice) => Practice.resolveDocumentSettings(practice))
    );

    this.patientDocument$
      .pipe(take(1), takeUntil(this._onDestroy$))
      .subscribe((patientDocument) =>
        this.templateForm.patchValue(patientDocument, { emitEvent: false })
      );

    validFormGroupChanges$(this.templateForm)
      .pipe(
        debounceTime(500),
        withLatestFrom(this.patientDocument$),
        takeUntil(this._onDestroy$)
      )
      // TODO: https://app.clickup.com/t/3mu7g4
      .subscribe(
        ([value, doc]) => void Firestore.saveDoc({ ...doc, ...value })
      );

    void wakeUpFunction('http-pdf-generatePdf');
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async print(): Promise<void> {
    const brand = await snapshot(this._brandScope.doc$.pipe(filterUndefined()));
    const templates = await this._resolveSystemTemplates(brand);
    const documentSettings = await snapshot(this.documentSettings$);
    const resolvedTemplates = this._resolveDefaultTemplates(templates);
    const content = this._buildPdfContent(resolvedTemplates, documentSettings);
    const extensions = this._editorExtensions.defaultToHTMLExtensions();
    await this.pdfService.print(content, extensions);
  }

  async printSettings(): Promise<void> {
    const brand = await snapshot(this._brandScope.doc$.pipe(filterUndefined()));
    const templates = await this._resolveSystemTemplates(brand);
    const documentSettings = await snapshot(this.documentSettings$);

    const result = await this._openPdfDialog(
      DocumentPdfDialogMode.Print,
      templates,
      documentSettings
    );
    if (!result) {
      return;
    }

    const settings = {
      ...documentSettings,
      ...omitBy(result.documentSettings, isNil),
    };
    const content = this._buildPdfContent(result.templates, settings);
    const extensions = this._editorExtensions.defaultToHTMLExtensions();
    await this.pdfService.print(content, extensions);
  }

  async generatePdf(): Promise<void> {
    const brand = await snapshot(this._brandScope.doc$.pipe(filterUndefined()));
    const templates = await this._resolveSystemTemplates(brand);
    const documentSettings = await snapshot(this.documentSettings$);
    const result = await this._openPdfDialog(
      DocumentPdfDialogMode.Pdf,
      templates,
      documentSettings
    );
    if (!result) {
      return;
    }

    const settings = {
      ...documentSettings,
      ...omitBy(result.documentSettings, isNil),
    };
    const content = this._buildPdfContent(result.templates, settings);
    const extensions = this._editorExtensions.defaultToHTMLExtensions();

    if (result.action === DocumentPdfAction.Download) {
      await this.pdfService.download(content, extensions, result.fileName);
    } else if (result.action === DocumentPdfAction.Save) {
      await this._saveToPatientFiles(content, extensions, result.fileName);
    }
  }

  async delete(): Promise<void> {
    const data: IConfirmationDialogData = confirmationDialogData({
      title: 'Delete Patient Document',
      prompt: 'Are you sure you want to delete this patient document?',
      submitLabel: 'Delete',
      submitColor: 'warn',
    });
    const confirmed = await this._dialog
      .open<ConfirmDialogComponent, IConfirmationDialogInput, boolean>(
        ConfirmDialogComponent,
        DialogPresets.small({ data })
      )
      .afterClosed()
      .toPromise();
    if (!confirmed) {
      return;
    }
    const patientDocument = await snapshot(this.patientDocument$);
    await Firestore.deleteDoc(patientDocument.ref);
    this._location.back();
  }

  private async _openPdfDialog(
    mode: DocumentPdfDialogMode,
    templates: ITemplateDefinitionWithName[],
    documentSettings: IDocumentSettings
  ): Promise<IDocumentPdfDialogResult | undefined> {
    const dialogData: IDocumentPdfDialogData = {
      mode,
      templates,
      documentName: this.templateForm.controls.name.value ?? '',
      documentSettings,
    };

    return this._dialog
      .open<
        DocumentPdfDialogComponent,
        IDocumentPdfDialogData,
        IDocumentPdfDialogResult
      >(DocumentPdfDialogComponent, DialogPresets.large({ data: dialogData }))
      .afterClosed()
      .toPromise();
  }

  private async _resolveSystemTemplates(
    brand: WithRef<IBrand>
  ): Promise<ITemplateDefinitionWithName[]> {
    return SystemTemplates.resolveSystemTemplates(brand, [
      SystemTemplate.PrintHeader,
      SystemTemplate.PrintFooter,
    ]);
  }

  private _resolveDefaultTemplates(
    templates: ITemplateDefinitionWithName[]
  ): IResolvedTemplateWithName[] {
    return templates
      .filter(({ templateDoc }) => !!getSchemaSize(templateDoc.content))
      .map(({ template, templateDoc }) => ({
        template,
        content: templateDoc.content,
      }));
  }

  private _buildPdfContent(
    templates: IResolvedTemplateWithName[],
    documentSettings: IDocumentSettings
  ): IPDFContent {
    const header = SystemTemplates.findTemplate(
      templates,
      SystemTemplate.PrintHeader
    );
    const footer = SystemTemplates.findTemplate(
      templates,
      SystemTemplate.PrintFooter
    );

    return {
      body: this.templateForm.controls.content.value ?? initVersionedSchema(),
      header,
      footer,
      documentSettings,
    };
  }

  private async _saveToPatientFiles(
    content: IPDFContent,
    extensions: AnyExtension[],
    fileName?: string
  ): Promise<void> {
    try {
      const patient = await snapshot(
        this._patientScope.doc$.pipe(filterUndefined())
      );

      await this.pdfService.saveToPatientFiles(
        patient.ref,
        content,
        extensions,
        fileName
      );
      this._snack.open('PDF saved to patient files');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save PDF to patient files', error);
      this._snack.open('Failed to save PDF to patient files');
    }
  }
}
